import { useState } from "react";
import { forgetSessionKeys, saveSessionKeys } from "../api";
import type { ProviderStatus } from "../shared/report";

interface ProviderPanelProps {
  status: ProviderStatus | null;
  onRefresh: () => Promise<void>;
}

export function ProviderPanel({ status, onRefresh }: ProviderPanelProps) {
  const [heliusKey, setHeliusKey] = useState("");
  const [jupiterKey, setJupiterKey] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!heliusKey.trim() && !jupiterKey.trim()) {
      setMessage("Enter a Helius or Jupiter API key first.");
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await saveSessionKeys({
        ...(heliusKey.trim() ? { heliusApiKey: heliusKey.trim() } : {}),
        ...(jupiterKey.trim() ? { jupiterApiKey: jupiterKey.trim() } : {}),
      });
      setHeliusKey("");
      setJupiterKey("");
      await onRefresh();
      setMessage("API keys are ready for this session.");
    } catch {
      setMessage("We couldn’t use those API keys. Check them and try again.");
    } finally {
      setBusy(false);
    }
  };

  const forget = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await forgetSessionKeys();
      setHeliusKey("");
      setJupiterKey("");
      await onRefresh();
      setMessage("API keys forgotten.");
    } catch {
      setMessage("We couldn’t forget those API keys. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="provider-panel">
      <summary>
        <span className="provider-summary-copy">
          <span className="provider-summary-title">Data connections</span>
          <span aria-live="polite" aria-atomic="true">{providerHeadline(status)}</span>
        </span>
        <span className="provider-summary-badge" aria-hidden="true">
          {status ? "View" : "Checking"}
        </span>
      </summary>

      <div className="provider-panel-body">
        <div className="provider-grid" role="group" aria-label="Live data status">
          {status ? (
            <>
              <ProviderCard name="Chain controls + holders" state={status.solanaRpc} />
              <ProviderCard name="Jupiter identity + exits" state={status.jupiter} />
              {status.coinGecko && (
                <ProviderCard name="CoinGecko identity check" state={status.coinGecko} />
              )}
              <ProviderCard name="Price comparison" state={status.dexScreener} />
            </>
          ) : (
            <p className="muted">Checking the live data sources…</p>
          )}
        </div>

        {status?.localByokEnabled && (
          <div className="key-box">
            <div>
              <p className="eyebrow">Optional</p>
              <h3>Use your own API keys</h3>
              <p>
                Helius can make chain checks more reliable. A Jupiter key can steady its identity and
                exit requests; the keyless endpoint remains available. Keys are used only for this session, are never shown back in the
                page, and disappear when you stop the local app.
              </p>
            </div>
            <div className="key-fields">
              <label>
                Helius API key
                <input
                  type="password"
                  value={heliusKey}
                  onChange={(event) => setHeliusKey(event.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder={status.sessionHasHeliusKey ? "Already set for this session" : "Optional"}
                  aria-describedby="provider-key-warning"
                />
              </label>
              <label>
                Jupiter API key
                <input
                  type="password"
                  value={jupiterKey}
                  onChange={(event) => setJupiterKey(event.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder={status.sessionHasJupiterKey ? "Already set for this session" : "Optional"}
                  aria-describedby="provider-key-warning"
                />
              </label>
            </div>
            <p className="key-warning" id="provider-key-warning">
              Provider API keys only. Never enter a wallet private key, seed phrase, recovery phrase,
              or signing credential.
            </p>
            <div className="button-row">
              <button className="button button-secondary" type="button" onClick={save} disabled={busy}>
                {busy ? "Working…" : "Save for this session"}
              </button>
              {(status.sessionHasHeliusKey || status.sessionHasJupiterKey) && (
                <button className="button button-quiet" type="button" onClick={forget} disabled={busy}>
                  Forget API keys
                </button>
              )}
            </div>
            <p
              className={message ? "form-message" : "sr-only"}
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {message ?? ""}
            </p>
          </div>
        )}
      </div>
    </details>
  );
}

function ProviderCard({
  name,
  state,
}: {
  name: string;
  state: ProviderStatus["solanaRpc"];
}) {
  return (
    <div className="provider-card">
      <div className="provider-card-heading">
        <span className={`status-dot status-dot-${state.mode}`} />
        <span>{name}</span>
      </div>
      <strong>{state.label}</strong>
      <p>{state.detail}</p>
    </div>
  );
}

function providerHeadline(status: ProviderStatus | null): string {
  return status ? "Live checks are ready" : "Checking availability…";
}
